import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { SEO } from '../../../utils/seo';

export default function CompliancePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Compliance Solutions",
    "provider": {
      "@type": "LocalBusiness",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "North Wales"
    },
    "description": "Expert guidance ensuring your business meets all regulatory requirements including GDPR, health & safety standards, and industry-specific compliance needs.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Compliance Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "GDPR Compliance",
            "description": "Comprehensive GDPR compliance services ensuring data processing meets all requirements"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Health & Safety Compliance",
            "description": "Expert health and safety compliance services covering workplace safety regulations"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Industry Standards",
            "description": "Guidance on achieving ISO standards and industry-specific certifications"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Compliance Solutions North Wales - GDPR Health Safety ISO - LEWIS SECURITY"
        description="Expert guidance ensuring your business meets all regulatory requirements including GDPR, health & safety standards, and industry-specific compliance needs. Professional compliance consultancy in North Wales."
        keywords="compliance solutions North Wales, GDPR compliance, health and safety, ISO standards, regulatory compliance, compliance consultancy"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-900 via-gray-900 to-gray-800">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-checkbox-circle-line text-5xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-poppins">Compliance Solutions</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter">
                Expert guidance ensuring your business meets all regulatory requirements including GDPR, health & safety standards, and industry-specific compliance needs
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 font-poppins">Navigate Compliance with Confidence</h2>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Regulatory compliance is increasingly complex, with businesses facing multiple overlapping requirements from various authorities. LEWIS SECURITY provides expert compliance consultancy to help you understand, implement, and maintain compliance across all relevant areas.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Our compliance specialists stay current with evolving regulations and industry standards, ensuring your business remains compliant while focusing on your core operations. We provide practical, cost-effective solutions tailored to your specific industry and business size.
                </p>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/fa694906524459198da0de20744d0e9c.jpg"
                  alt="Compliance Solutions"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Compliance Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shield-user-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">GDPR Compliance</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Comprehensive GDPR compliance services ensuring your data processing activities meet all requirements of the General Data Protection Regulation. We help you protect customer data and avoid significant penalties.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Data protection impact assessments</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Privacy policy development</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Data processing agreements</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Staff training and awareness</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-first-aid-kit-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Health & Safety</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Expert health and safety compliance services covering all aspects of workplace safety regulations. We help you create safe working environments and meet your legal obligations under UK health and safety law.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Health & safety policy development</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Risk assessments and method statements</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Accident investigation and reporting</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Safety training and certification</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-building-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Industry Standards</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Guidance on achieving and maintaining industry-specific standards and certifications. We help you meet the requirements of ISO standards, industry accreditations, and sector-specific regulations.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>ISO 9001 quality management</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>ISO 27001 information security</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Industry accreditation support</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Audit preparation and support</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-list-3-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Documentation & Records</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Comprehensive documentation services ensuring all required policies, procedures, and records are properly maintained. We help you create audit-ready documentation that demonstrates compliance.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Policy and procedure development</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Compliance documentation systems</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Record keeping frameworks</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Document control procedures</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Compliance Areas */}
            <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Key Compliance Areas We Cover</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <i className="ri-shield-check-line text-3xl text-red-600 mb-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Data Protection</h3>
                  <p className="text-sm text-gray-600 font-inter">GDPR, Data Protection Act 2018, privacy regulations</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <i className="ri-fire-line text-3xl text-red-600 mb-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Fire Safety</h3>
                  <p className="text-sm text-gray-600 font-inter">Regulatory Reform (Fire Safety) Order 2005</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <i className="ri-first-aid-kit-line text-3xl text-red-600 mb-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Workplace Safety</h3>
                  <p className="text-sm text-gray-600 font-inter">Health and Safety at Work Act 1974</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <i className="ri-building-2-line text-3xl text-red-600 mb-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Building Regulations</h3>
                  <p className="text-sm text-gray-600 font-inter">Construction and building safety compliance</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <i className="ri-lock-line text-3xl text-red-600 mb-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Security Standards</h3>
                  <p className="text-sm text-gray-600 font-inter">NSI, SSAIB, BAFE accreditation requirements</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <i className="ri-file-shield-line text-3xl text-red-600 mb-3"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Environmental</h3>
                  <p className="text-sm text-gray-600 font-inter">Environmental protection and waste management</p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Why Compliance Matters</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Avoid Penalties</h3>
                  <p className="text-sm text-gray-600 font-inter">Non-compliance can result in significant fines, legal action, and reputational damage</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-trophy-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Competitive Advantage</h3>
                  <p className="text-sm text-gray-600 font-inter">Demonstrate professionalism and win more business with proper certifications</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-heart-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Peace of Mind</h3>
                  <p className="text-sm text-gray-600 font-inter">Focus on your business knowing you're meeting all legal obligations</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gray-900 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-poppins">Ensure Your Business Compliance</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto font-inter">
                Don't risk penalties and legal issues. Contact us today for expert compliance guidance tailored to your business
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Get Compliance Review
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:07834561015" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 07834 561015
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
